import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import queryString from 'query-string';
import { Device, Software } from '../types/Device';
import { Category, Product } from '../types/Product';
import { Tutorial } from '../types/Tutorial';

type TutorialResponse = {
  success: boolean;
  data: TutorialResponseData;
  message: null | string;
};

type TutorialResponseData = { count: number; currentPage: number; limit: number; result: Tutorial[] };

type TutorialQueryArgs = {
  page?: number;
};
type ContactResponse = {
  success: boolean;
  data: Record<string, never>;
  message: string;
};
type ContactQueryArgs = {
  name: string;
  email: string;
  message: string;
};

type FeaturedProductsResponse = {
  success: boolean;
  data: Product[];
  message: string;
};

type ProductsResponse = {
  success: boolean;
  data: ProductResponseData;
  message: string;
};

type ProductResponseData = {
  result: Product[];
  count: number;
  currentPage: number;
  limit: number;
};
type ProductsQueryArgs = {
  page?: string;
  limit?: string;
  search?: string;
  orderBy?: string;
  sortBy?: string;
  parentId?: string;
  categoryId?: string;
};
type ProductsLineupResponse = FeaturedProductsResponse;

type CategoriesResponse = {
  success: boolean;
  data: Category[];
  message: string;
};

type DevicesResponse = {
  success: boolean;
  data: Device[];
  message: string | null;
};

type SoftwaresQueryArgs = {
  page?: string;
  limit?: string;
  orderBy?: string;
  sortBy?: string;
  search?: string;
  deviceId?: string;
};

type SoftwareResponseData = {
  result: Software[];
  count: number;
  currentPage: number;
  limit: number;
};

type SoftwaresResponse = {
  success: boolean;
  data: SoftwareResponseData;
  message: string;
};
// Define a service using a base URL and expected endpoints
export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL }),
  endpoints: (builder) => ({
    getTutorials: builder.query<TutorialResponseData, TutorialQueryArgs>({
      query: ({ page = 1 }) => `tutorials?${queryString.stringify({ limit: 8, page })}`,
      transformResponse: (response: TutorialResponse) => response.data,
    }),
    getFeaturedTutorial: builder.query<Tutorial, string>({
      query: () => `tutorials/featured`,
      transformResponse: (response: { success: boolean; data: Tutorial; message: string }) => response.data,
    }),
    getFeaturedProducts: builder.query<Product[], string>({
      query: () => `products/featured`,
      transformResponse: (response: FeaturedProductsResponse) => response.data,
    }),
    getProductsLineup: builder.query<Product[], string>({
      query: () => `products/lineup`,
      transformResponse: (response: ProductsLineupResponse) => response.data,
    }),
    getProducts: builder.query<ProductResponseData, ProductsQueryArgs>({
      query: ({ categoryId, limit = 6, orderBy, page = 1, parentId, search, sortBy }) => {
        return `products?${queryString.stringify({ categoryId, limit, orderBy, page, parentId, search, sortBy })}`;
      },
      transformResponse: (response: ProductsResponse) => response.data,
    }),
    getCategories: builder.query<Category[], string>({
      query: () => `categories`,
      transformResponse: (response: CategoriesResponse) => response.data,
    }),

    getDevices: builder.query<Device[], string>({
      query: () => `devices`,
      transformResponse: (response: DevicesResponse) => response.data,
    }),

    getSoftwares: builder.query<SoftwareResponseData, SoftwaresQueryArgs>({
      query: (args) => {
        return `softwares?${queryString.stringify(args)}`;
      },
      transformResponse: (response: SoftwaresResponse) => response.data,
    }),

    getSoftware: builder.query<Software, { softwareId: string }>({
      query: (args) => {
        return `softwares/${args.softwareId}`;
      },
      transformResponse: (response: { success: boolean; data: Software; message: string }) => response.data,
    }),

    getProduct: builder.query<Product, string>({
      query: (id) => {
        return `products/${id}`;
      },
      transformResponse: (response: { success: boolean; data: Product; message: string }) => response.data,
    }),
    getSimilarProducts: builder.query<Product[], string>({
      query: (id) => {
        return `products/similar/${id}`;
      },
      transformResponse: (response: { success: boolean; data: Product[]; message: string }) => response.data,
    }),
    postContact: builder.mutation<ContactResponse, ContactQueryArgs>({
      query: (body) => ({
        url: `contact`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetTutorialsQuery,
  usePostContactMutation,
  useGetFeaturedProductsQuery,
  useGetProductsLineupQuery,
  useGetProductsQuery,
  useGetCategoriesQuery,
  useGetDevicesQuery,
  useGetSoftwaresQuery,
  useGetSoftwareQuery,
  useGetProductQuery,
  useGetSimilarProductsQuery,
  useGetFeaturedTutorialQuery,
} = api;
